import styled from '@emotion/styled';
import { Button, Switch } from 'antd';
import LeftArrow from 'assets/svg/LeftArrow';
import RightArrowAlt from 'assets/svg/RightArrowAlt';
import RightArrow from 'assets/svg/RightArrow';
import { colorConstants } from 'config/colorConstants';
import dayjs from 'dayjs';
import React, { FC, useEffect, useRef, useState } from 'react';
import tw from 'twin.macro';
import { dayType, getAllDaysInYear, monthData } from 'utils/Calender';
import { addPrecedingZero, calculateFontSize } from 'utils/helperMethods';

const CustomButton = styled(Button)`
    &.ant-btn[disabled] {
        ${tw`bg-borderGrey text-promoColour text-[13px] rounded font-bold`}
    }
    &.ant-btn:focus:not(:disabled),
    &.ant-btn:hover:not(:disabled) {
        ${tw`border-purpleFont text-white opacity-80 bg-purpleFont`}
    }
    ${tw`py-3 rounded h-auto bg-purpleFont text-white font-bold`}
`;

const firstSixMonths = monthData.slice(0, monthData.length / 2);
const lastSixMonths = monthData.slice(monthData.length / 2);

const currentYear = new Date().getFullYear();

interface CalenderProps {
    startYear?: number;
    minYear?: number;
    maxYear?: number;
    range?: DateRangeType;
    onChange?: (range: DateRangeType) => void;
    onFooterClick?: () => void;
    startDay?: dayType;
    disabledDates?: [number | string | null, number | string | null]; //provide [startTime,endTime]
    singleDateSelect?: boolean;
    showSingleSelectOption?: boolean;
    singleSelectOptionChange?: Function;
}

export type DateRangeType = [Date | null, Date | null];
export type DisabledDatesType = [number | string | null, number | string | null];

const defaultMonthsMapping: any = {
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
    7: [],
    8: [],
    9: [],
    10: [],
    11: [],
    12: [],
};

type itemType = {
    label: string;
    key: number;
};

const checkIfDatesEqual = (range: DateRangeType, date: Date) => {
    if (range[0]?.getTime() === date.getTime()) return 0;
    if (range[1]?.getTime() === date.getTime()) return 1;
    return false;
};
const buttonFont = calculateFontSize(window.innerWidth, 13);

const Calender: FC<CalenderProps> = ({
    startYear = currentYear,
    maxYear = startYear,
    minYear = currentYear - 3,
    range = [null, null],
    startDay,
    onChange = (arr) => {},
    onFooterClick = () => {},
    disabledDates = [null, null], //pass the dates in  mm-dd-yyyy or mm/dd/yyyy format
    singleDateSelect = false,
    showSingleSelectOption,
    singleSelectOptionChange,
}) => {
    const [selectedYear, setSelectedYear] = useState(startYear);
    const [monthsDaysMapping, setMonthsDaysMapping] = useState(defaultMonthsMapping);
    const monthsDaysMappingCache = useRef<any>({});
    useEffect(() => {
        const cacheKey = `${selectedYear}-${startDay}`; // cache key

        let allDays;
        //check in cache
        if (cacheKey in monthsDaysMappingCache.current) {
            allDays = monthsDaysMappingCache.current[cacheKey];
        } else {
            allDays = getAllDaysInYear(selectedYear, startDay);
            monthsDaysMappingCache.current[cacheKey] = allDays;
        }
        console.log(startDay, 'logging f');
        setMonthsDaysMapping(allDays);
    }, [selectedYear, startDay]);

    const yearClickHandler = (direction: 'left' | 'right') => {
        if (direction === 'left' && selectedYear > minYear) {
            return setSelectedYear(selectedYear - 1);
        }
        if (direction === 'right' && selectedYear < maxYear) {
            return setSelectedYear(selectedYear + 1);
        }
    };

    //if selected date is less than start date, change start date else change end date
    const placeSelectedDate = (date: Date, range: DateRangeType) => {
        if (date.getTime() <= Number(range[0]?.getTime())) return 0;
        return 1;
    };

    const onChangeHandler = (item: itemType, day: any, isDisabled: boolean) => {
        if (isDisabled) return;
        const temp: DateRangeType = [...range];
        const isDatesEqual = checkIfDatesEqual(temp, day.date);
        if (!singleDateSelect) {
            //check if user is clicking on the same date
            //if true, revert the action (unselect)
            if (isDatesEqual !== false) {
                temp[isDatesEqual] = null;
                //check if first value is null
                //if true, it means add the start date
            } else if (temp[0] === null) {
                //check if selected date is greater than end date
                //if true, swap the start date and end date
                if (temp[1] && new Date(day.date).getTime() > temp[1].getTime()) {
                    const swapDate = temp[1];
                    temp[0] = swapDate;
                    temp[1] = new Date(day.date);
                } else temp[0] = new Date(day.date);
            } else if (temp[1] === null) {
                //check if selected date is less than start date
                //if true, swap the start date and end date
                if (new Date(day.date).getTime() < temp[0].getTime()) {
                    const swapDate = temp[0];
                    temp[1] = swapDate;
                    temp[0] = new Date(day.date);
                } else temp[1] = new Date(day.date);
                //check if both values present
            } else if (temp[0] && temp[1]) {
                //if selected date is not equal to start date and end date, adjust date accordingly
                if (isDatesEqual === false) {
                    const index: number = placeSelectedDate(day.date, range);
                    temp[index] = new Date(day.date);
                    // revert the action (unselect)
                } else {
                    temp[isDatesEqual] = null;
                }
            }
        } else {
            const index: number = placeSelectedDate(day.date, range);
            temp[index] = new Date(day.date);
        }

        return onChange(temp);
    };

    return (
        <section className="calender-width">
            {/* HEADER */}
            <div className=" flex justify-center items-center gap-1 text-center">
                <span
                    className={` p-1
                ${selectedYear === minYear ? 'cursor-not-allowed ' : ' hover:scale-125 cursor-pointer'}
                `}
                    onClick={() => yearClickHandler('left')}
                >
                    <LeftArrow stroke={selectedYear === minYear ? colorConstants.lightGrey : colorConstants.fontGrey} />
                </span>
                <h2 className=" text-greyFont m-0 font-sm font-medium select-none">{selectedYear}</h2>
                <span
                    className={` p-1
                ${selectedYear === maxYear ? 'cursor-not-allowed ' : ' hover:scale-125 cursor-pointer'}
                `}
                    onClick={() => yearClickHandler('right')}
                >
                    <RightArrow
                        stroke={selectedYear === maxYear ? colorConstants.lightGrey : colorConstants.fontGrey}
                    />
                </span>
            </div>
            {showSingleSelectOption && (
                <div className=" absolute right-3 top-3">
                    <span className="font-xs mr-1">Single Select</span>{' '}
                    <Switch
                        checked={singleDateSelect}
                        onChange={(val) => {
                            if (singleSelectOptionChange) singleSelectOptionChange(val);
                        }}
                    />
                </div>
            )}

            {/* MAIN SECTION */}
            <div className="flex flex-1  justify-between items-center mt-6">
                <div className="border-r w-[50%] border-borderLinePurpuleAlt">
                    {firstSixMonths.map((item) => {
                        return (
                            <CalenderRow
                                key={item.key}
                                item={item}
                                onChange={onChangeHandler}
                                days={monthsDaysMapping[item.key]}
                                range={range}
                                disabledDates={disabledDates}
                            />
                        );
                    })}
                </div>
                <div className="basis-[content]  w-[50%]">
                    {lastSixMonths.map((item) => {
                        return (
                            <CalenderRow
                                key={item.key}
                                item={item}
                                onChange={onChangeHandler}
                                days={monthsDaysMapping[item.key]}
                                range={range}
                                disabledDates={disabledDates}
                            />
                        );
                    })}
                </div>
            </div>
            {/* FOOTER */}
            <div className="flex justify-center mt-5">
                <CustomButton
                    onClick={() => onFooterClick()}
                    disabled={singleDateSelect ? range[1] == null : range.includes(null)}
                    className="min-w-[200px] font-xs"
                    style={{ fontSize: buttonFont }}
                >
                    Set Date
                </CustomButton>
            </div>
        </section>
    );
};

export default React.memo(Calender);

type RowProps = {
    item: itemType;
    days: any;
    onChange: Function;
    range: DateRangeType;
    disabledDates: DisabledDatesType;
};
const checkIfDateBetweenSelectedDates = (range: DateRangeType, date: Date) => {
    if (range[0] === null || range[1] === null) return false;
    if (range[0].getTime() < date.getTime() && range[1].getTime() > date.getTime()) return true;
    return false;
};

const checkIfCellToBeDisabled = (disabledDates: DisabledDatesType, date: any): boolean => {
    let isDisabled = false;
    if (disabledDates[0] && date?.date < new Date(disabledDates[0])) isDisabled = true;
    if (disabledDates[1] && date?.date > new Date(disabledDates[1])) isDisabled = true;
    return isDisabled;
};

const CalenderRow = ({ item, days, onChange, range = [null, null], disabledDates }: RowProps) => {
    const { label, key } = item;
    return (
        <div className={`p-1 select-none flex gap-1 items-center`}>
            <p className="text-greyFont calender-item-width font-xxs p-[9px] font-bold">{label}</p>
            <div className="flex justify-center items-center">
                {days.length > 0 &&
                    days.map((day: any) => {
                        const isDisabled = checkIfCellToBeDisabled(disabledDates, day);
                        const isDatesEqual = checkIfDatesEqual(range, day.date);
                        return (
                            <div
                                key={day.date}
                                onClick={() => onChange(item, day, isDisabled)}
                                className={` cursor-pointer rounded-[50%] calender-item-width hover:scale-110 p-[9px] text-greyFont font-xxs font-medium text-center
                                ${
                                    isDisabled
                                        ? 'cursor-not-allowed hover:scale-100 !text-promoColour font-thin'
                                        : isDatesEqual !== false
                                        ? 'bg-profileInitials !text-white'
                                        : !checkIfDateBetweenSelectedDates(range, day.date) && !range.includes(null)
                                        ? '!text-promoColour font-light'
                                        : ''
                                }
                                `}
                            >
                                {addPrecedingZero(day?.day)}
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};
